import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../services/apiService';
import { ChatSearchResult } from '../../types';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Appelé quand l'user clique un résultat. Le parent gère la navigation
   *  vers la vue Messagerie avec la conv + le message ciblés. */
  onJumpTo: (conversationId: string, messageId: string) => void;
}

const DEBOUNCE_MS = 300;
const MIN_QUERY_LEN = 2;

function formatDate(iso: string): string {
  const d = new Date(iso.replace(' ', 'T'));
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) +
         ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function highlight(content: string, query: string): React.ReactNode[] {
  const terms = query.trim().split(/\s+/).filter(t => t.length >= 2);
  if (terms.length === 0) return [content];
  const regex = new RegExp(`(${terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'ig');
  return content.split(regex).map((part, i) =>
    regex.test(part)
      ? <mark key={i} className="bg-cyan-200 dark:bg-cyan-400/30 text-cyan-900 dark:text-cyan-100 rounded-sm px-0.5">{part}</mark>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );
}

const GlobalChatSearchModal: React.FC<Props> = ({ open, onClose, onJumpTo }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ChatSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debounceRef = useRef<number | null>(null);

  // Reset au close + focus à l'ouverture
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
      setResults([]);
      setError(null);
      setActiveIdx(0);
    }
  }, [open]);

  // Recherche debouncée
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < MIN_QUERY_LEN) {
      setResults([]);
      setError(null);
      setLoading(false);
      return;
    }
    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.chatSearch(q, 30);
        setResults(res);
        setError(null);
        setActiveIdx(0);
      } catch (err: any) {
        setError(err?.message || 'Erreur de recherche');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query, open]);

  // Navigation clavier
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (results.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx(i => Math.min(i + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const r = results[activeIdx];
        if (r) {
          onJumpTo(r.conversation_id, r.id);
          onClose();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, results, activeIdx, onJumpTo, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] bg-slate-900/50 dark:bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl mx-4 rounded-2xl border border-slate-200 dark:border-[rgba(100,140,210,0.3)] bg-white dark:bg-[rgba(15,30,60,0.97)] shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 dark:border-[rgba(100,140,210,0.2)]">
          <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Rechercher dans toutes les conversations…"
            className="flex-1 bg-transparent outline-none text-sm text-slate-900 dark:text-[#e0eaff] placeholder:text-slate-400 dark:placeholder:text-[#546a96]"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[10px] font-mono text-slate-500 dark:text-[#7f93bc]">
            ESC
          </kbd>
        </div>

        {/* Résultats */}
        <div className="max-h-[60vh] overflow-y-auto">
          {query.trim().length < MIN_QUERY_LEN ? (
            <div className="p-6 text-center text-xs text-slate-400 dark:text-[#546a96]">
              Tapez au moins {MIN_QUERY_LEN} caractères pour rechercher.
            </div>
          ) : loading ? (
            <div className="p-6 text-center text-xs text-slate-500 dark:text-[#546a96]">Recherche…</div>
          ) : error ? (
            <div className="p-6 text-center text-xs text-rose-600 dark:text-rose-300">{error}</div>
          ) : results.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-500 dark:text-[#546a96]">Aucun résultat.</div>
          ) : (
            results.map((r, i) => (
              <button
                key={r.id}
                type="button"
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => { onJumpTo(r.conversation_id, r.id); onClose(); }}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 dark:border-[rgba(100,140,210,0.1)] transition-colors ${
                  i === activeIdx
                    ? 'bg-cyan-50 dark:bg-cyan-500/10'
                    : 'hover:bg-slate-50 dark:hover:bg-[rgba(100,140,210,0.06)]'
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-[11px] font-bold text-cyan-600 dark:text-cyan-300 truncate">
                    {r.conv_type === 'group' ? `# ${r.conv_name || 'Groupe'}` : `💬 Conversation directe`}
                  </span>
                  <span className="text-[10px] text-slate-400 dark:text-[#546a96] flex-shrink-0">
                    {formatDate(r.created_at)}
                  </span>
                </div>
                <div className="text-xs text-slate-900 dark:text-[#e0eaff] line-clamp-2">
                  <span className="text-slate-500 dark:text-[#7f93bc] font-medium">{r.sender_name} : </span>
                  {highlight(r.content, query)}
                </div>
              </button>
            ))
          )}
        </div>

        {/* Footer raccourcis */}
        <div className="flex items-center justify-between gap-3 px-4 py-2 border-t border-slate-100 dark:border-[rgba(100,140,210,0.2)] bg-slate-50 dark:bg-[rgba(15,30,60,0.5)] text-[10px] text-slate-500 dark:text-[#7f93bc]">
          <div className="flex items-center gap-3">
            <span><kbd className="font-mono">↑</kbd> <kbd className="font-mono">↓</kbd> naviguer</span>
            <span><kbd className="font-mono">⏎</kbd> ouvrir</span>
          </div>
          <span>{results.length > 0 ? `${results.length} résultat(s)` : ''}</span>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GlobalChatSearchModal;
